import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Trash2, Phone, MapPin, Mail, Package } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  new: { label: "Yeni", color: "bg-amber-50 text-amber-700" },
  contacted: { label: "Arandı", color: "bg-blue-50 text-blue-700" },
  confirmed: { label: "Onaylandı", color: "bg-emerald-50 text-emerald-700" },
  shipped: { label: "Kargolandı", color: "bg-purple-50 text-purple-700" },
  cancelled: { label: "İptal", color: "bg-red-50 text-red-700" },
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}/status`, { status });
      toast.success("Durum güncellendi");
      await load();
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Siparişi silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/admin/orders/${id}`);
      toast.success("Silindi");
      if (selected?.id === id) setSelected(null);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-8" data-testid="admin-orders-page">
      <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#059669] mb-2">Ön Sipariş</p>
      <h1 className="font-display text-3xl md:text-4xl tracking-tighter font-black mb-8">Siparişler</h1>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-5 border border-gray-200 bg-white">
          <div className="px-4 py-3 border-b border-gray-200 text-xs uppercase tracking-[0.18em] text-gray-500">
            {orders.length} sipariş
          </div>
          <div className="divide-y divide-gray-100 max-h-[70vh] overflow-auto">
            {orders.map((o) => {
              const totalQty = o.items?.reduce((a, b) => a + (b.quantity || 0), 0) || 0;
              const s = STATUS[o.status] || STATUS.new;
              return (
                <button
                  key={o.id}
                  onClick={() => setSelected(o)}
                  data-testid={`order-${o.id}`}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 ${selected?.id === o.id ? "bg-emerald-50" : ""}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-display tracking-tight text-sm">{o.customer_name}</p>
                    <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 ${s.color}`}>{s.label}</span>
                  </div>
                  <p className="text-xs text-gray-500">{o.customer_phone} · {totalQty} ürün</p>
                  <p className="text-[10px] font-mono-fd text-gray-400 mt-1">{new Date(o.created_at).toLocaleString("tr-TR")}</p>
                </button>
              );
            })}
            {orders.length === 0 && <div className="px-4 py-10 text-center text-gray-400 text-sm">Henüz sipariş yok</div>}
          </div>
        </div>

        <div className="lg:col-span-7 border border-gray-200 bg-white p-6 min-h-[60vh]">
          {selected ? (
            <div>
              <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-gray-500">Müşteri</p>
                  <p className="font-display text-2xl tracking-tighter">{selected.customer_name}</p>
                </div>
                <span className={`text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 ${STATUS[selected.status]?.color}`}>
                  {STATUS[selected.status]?.label}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-sm">
                <a href={`tel:${selected.customer_phone}`} className="flex items-start gap-2 hover:text-[#059669]">
                  <Phone size={14} className="text-gray-400 mt-1" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-gray-500">Telefon</p>
                    <p>{selected.customer_phone}</p>
                  </div>
                </a>
                {selected.customer_email && (
                  <a href={`mailto:${selected.customer_email}`} className="flex items-start gap-2 hover:text-[#059669]">
                    <Mail size={14} className="text-gray-400 mt-1" />
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-gray-500">E-posta</p>
                      <p>{selected.customer_email}</p>
                    </div>
                  </a>
                )}
                <div className="flex items-start gap-2 md:col-span-2">
                  <MapPin size={14} className="text-gray-400 mt-1" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-gray-500">Adres</p>
                    <p className="whitespace-pre-wrap">{selected.address}</p>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-[10px] uppercase tracking-[0.18em] text-gray-500 mb-2 flex items-center gap-2">
                  <Package size={12} /> Ürünler
                </p>
                <div className="border border-gray-200 divide-y divide-gray-100">
                  {selected.items?.map((it, i) => (
                    <div key={i} className="px-4 py-2.5 flex items-center justify-between">
                      <span className="font-display tracking-tight text-sm">{it.product_name}</span>
                      <span className="text-sm font-mono-fd">{it.quantity} adet</span>
                    </div>
                  ))}
                </div>
              </div>

              {selected.notes && (
                <div className="mb-6">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-gray-500 mb-1">Notlar</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{selected.notes}</p>
                </div>
              )}

              <div className="flex flex-wrap gap-2 border-t border-gray-200 pt-5">
                {Object.entries(STATUS).map(([key, s]) => (
                  <button
                    key={key}
                    onClick={() => setStatus(selected.id, key)}
                    disabled={selected.status === key}
                    data-testid={`status-${key}`}
                    className={`px-3 py-2 text-xs uppercase tracking-[0.15em] font-semibold border ${
                      selected.status === key ? "bg-black text-white border-black" : "border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
                <button
                  onClick={() => remove(selected.id)}
                  className="ml-auto inline-flex items-center gap-1 text-red-600 px-3 py-2 text-xs uppercase tracking-[0.15em] font-semibold hover:bg-red-50"
                >
                  <Trash2 size={12} /> Sil
                </button>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-400 text-sm">Bir sipariş seçin</div>
          )}
        </div>
      </div>
    </div>
  );
}
